import { useCallback, useEffect, useState } from "react";
import type { ObjktToken, WalletNft } from "../../shared/types";
import {
  fetchHeldGlbsPage,
  fetchHeldNftsPage,
  type Session,
} from "../lib/api";

const SCULPTURES_PAGE_SIZE = 30;
const PAINTINGS_PAGE_SIZE = 30;

export function useInventory(session: Session | null) {
  const [sculptures, setSculptures] = useState<ObjktToken[]>([]);
  const [paintings, setPaintings] = useState<WalletNft[]>([]);
  const [loadingSculptures, setLoadingSculptures] = useState(false);
  const [loadingPaintings, setLoadingPaintings] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [sculpturePage, setSculpturePage] = useState(0);
  const [paintingPage, setPaintingPage] = useState(0);
  const [sculptureHasMore, setSculptureHasMore] = useState(false);
  const [paintingHasMore, setPaintingHasMore] = useState(false);

  useEffect(() => {
    setSculptures([]);
    setPaintings([]);
    setLoadingSculptures(false);
    setLoadingPaintings(false);
    setLoaded(false);
    setSculpturePage(0);
    setPaintingPage(0);
    setSculptureHasMore(false);
    setPaintingHasMore(false);
  }, [session?.address, session?.token]);

  const loadSculpturesPage = useCallback(
    async (page: number) => {
      if (!session) return;
      const nextPage = Math.max(0, page);
      setLoadingSculptures(true);
      try {
        const result = await fetchHeldGlbsPage(session, {
          limit: SCULPTURES_PAGE_SIZE,
          offset: nextPage * SCULPTURES_PAGE_SIZE,
        });
        setSculptures(result.tokens);
        setSculptureHasMore(result.pagination.hasMore);
        setSculpturePage(nextPage);
      } finally {
        setLoadingSculptures(false);
      }
    },
    [session]
  );

  const loadPaintingsPage = useCallback(
    async (page: number) => {
      if (!session) return;
      const nextPage = Math.max(0, page);
      setLoadingPaintings(true);
      try {
        const result = await fetchHeldNftsPage(session, {
          limit: PAINTINGS_PAGE_SIZE,
          offset: nextPage * PAINTINGS_PAGE_SIZE,
        });
        setPaintings(result.tokens);
        setPaintingHasMore(result.pagination.hasMore);
        setPaintingPage(nextPage);
      } finally {
        setLoadingPaintings(false);
      }
    },
    [session]
  );

  const load = useCallback(async () => {
    if (!session || loaded) return;
    await Promise.all([loadSculpturesPage(0), loadPaintingsPage(0)]);
    setLoaded(true);
  }, [session, loaded, loadSculpturesPage, loadPaintingsPage]);

  const refresh = useCallback(async () => {
    if (!session) return;
    await Promise.all([
      loadSculpturesPage(sculpturePage),
      loadPaintingsPage(paintingPage),
    ]);
    setLoaded(true);
  }, [session, sculpturePage, paintingPage, loadSculpturesPage, loadPaintingsPage]);

  const nextSculpturesPage = useCallback(async () => {
    if (loadingSculptures || !sculptureHasMore) return;
    await loadSculpturesPage(sculpturePage + 1);
    setLoaded(true);
  }, [loadingSculptures, sculptureHasMore, sculpturePage, loadSculpturesPage]);

  const prevSculpturesPage = useCallback(async () => {
    if (loadingSculptures || sculpturePage <= 0) return;
    await loadSculpturesPage(sculpturePage - 1);
    setLoaded(true);
  }, [loadingSculptures, sculpturePage, loadSculpturesPage]);

  const nextPaintingsPage = useCallback(async () => {
    if (loadingPaintings || !paintingHasMore) return;
    await loadPaintingsPage(paintingPage + 1);
    setLoaded(true);
  }, [loadingPaintings, paintingHasMore, paintingPage, loadPaintingsPage]);

  const prevPaintingsPage = useCallback(async () => {
    if (loadingPaintings || paintingPage <= 0) return;
    await loadPaintingsPage(paintingPage - 1);
    setLoaded(true);
  }, [loadingPaintings, paintingPage, loadPaintingsPage]);

  return {
    sculptures,
    paintings,
    loadingSculptures,
    loadingPaintings,
    loading: loadingSculptures || loadingPaintings,
    loaded,
    sculpturePage,
    paintingPage,
    sculptureHasMore,
    paintingHasMore,
    load,
    refresh,
    nextSculpturesPage,
    prevSculpturesPage,
    nextPaintingsPage,
    prevPaintingsPage,
  };
}
